/**
 * Persona attribution v2 — Unit 2B. LIVE AND VERIFIED.
 *
 * Owns: app-proxy resolution, private cart claim writes, line-item binding
 * inspection, the product-form add guard, QR fragment handling, the
 * cart/minicart checkout guard and the record-confirmation step (Unit 2D2):
 * a successful PIN/QR resolution is only a PENDING confirmation until the
 * customer explicitly confirms the displayed record. The claim is written and
 * the binding becomes usable only inside that explicit confirmation.
 *
 * ENDPOINT
 * --------
 * PROXY_PATH below is the single source of truth for the resolver endpoint.
 * The Shopify app proxy is configured with the matching subpath prefix
 * ("apps") and subpath ("persona"), so POST /apps/persona/attribution/resolve
 * reaches the live resolver. The backend answers 404 while its own readiness
 * flag or any dependency is missing, so a misconfiguration still fails closed:
 * PIN and QR validation become unavailable rather than permissive, and no
 * credential is ever sent anywhere else. Nothing here configures the proxy.
 *
 * A successful resolve is not a read: it mints an attribution claim. So this
 * module is the only place a Persona credential is resolved, and it happens
 * once, on the product page. Nothing else — the Persona landing included —
 * may call resolveCredential() to inspect or preview a credential.
 *
 * TRUST MODEL
 * -----------
 * The claim is the only attribution evidence and is written exclusively to the
 * private cart attribute. The record binding is untrusted UX data used for
 * equality comparison only — never parsed, never treated as evidence. Raw PINs
 * and QR tokens are never persisted and never logged, and neither are claims,
 * bindings or resolver responses. Record confirmation display values
 * (initials, issuing funeral home) live only in the in-memory resolution and
 * in visible text nodes while the confirmation state is shown — never in
 * storage, URLs, cookies, form values or logs.
 */
(function () {
  'use strict';

  // Idempotent: safe to load more than once and after section reloads.
  if (window.PersonaAttributionV2) return;

  var PROXY_PATH = '/apps/persona/attribution/resolve';
  var CLAIM_ATTRIBUTE = '__persona_attribution_claim';
  var BINDING_PROPERTY = '_Persona Record Binding';
  var BINDING_PATTERN = /^prb1_[A-Za-z0-9_-]{43}$/;
  var QR_FRAGMENT_KEY = 'persona_qr';
  var BINDING_INPUT_SELECTOR = 'input[name="properties[' + BINDING_PROPERTY + ']"]';

  // Fixed customer-facing literals. Rendered with textContent only.
  var MESSAGES = {
    mismatch:
      'This cart already contains jewelry for a different Persona record. ' +
      'Complete that order first, or clear the cart before using another PIN.',
    unusable:
      'We could not confirm the Persona record on this cart. Please review or clear your cart and try again.',
    expired: 'Your Persona validation has expired. Please validate your PIN again.',
    failed: 'We could not complete this step. Please try again.',
    incomplete:
      'Validate your Persona PIN, upload a fingerprint, or choose to continue without an engraving.',
    qrFailed: 'We could not validate that link. Please enter your Persona PIN.',
    confirmRequired: 'Please confirm the Persona record before adding to cart.'
  };

  // ---------------------------------------------------------------------------
  // Resolution state (in memory only, keyed by persona container element)
  // ---------------------------------------------------------------------------
  var resolutions = new WeakMap();
  var expiryTimers = new WeakMap();
  var pendingQr = null;

  function isLive(resolution) {
    return !!resolution && resolution.expiresAt > Date.now();
  }

  function setResolution(container, resolution, onExpire) {
    if (!container || !resolution) return;
    clearExpiryTimer(container);
    clearStaleFormMessage(container);
    resolutions.set(container, resolution);
    var delay = Math.min(resolution.expiresAt - Date.now(), 2147483647);
    if (delay <= 0) {
      resolutions.delete(container);
      if (typeof onExpire === 'function') onExpire();
      return;
    }
    expiryTimers.set(
      container,
      setTimeout(function () {
        resolutions.delete(container);
        expiryTimers.delete(container);
        clearStaleFormMessage(container);
        if (typeof onExpire === 'function') onExpire();
      }, delay)
    );
  }

  function clearExpiryTimer(container) {
    var timer = expiryTimers.get(container);
    if (timer) clearTimeout(timer);
    expiryTimers.delete(container);
  }

  function getResolution(container) {
    var resolution = resolutions.get(container);
    if (!isLive(resolution)) {
      if (resolution) resolutions.delete(container);
      return null;
    }
    return resolution;
  }

  /**
   * Drop the current unsaved resolution. The private claim is cleared only when
   * the live cart holds no Persona-bound lines, so a claim supporting items
   * already in the cart is never erased. Best effort — the add guard re-checks
   * authoritatively before anything is submitted.
   */
  function clearResolution(container) {
    if (container) {
      clearExpiryTimer(container);
      clearStaleFormMessage(container);
      resolutions.delete(container);
      var input = container.closest('form')
        ? container.closest('form').querySelector(BINDING_INPUT_SELECTOR)
        : null;
      if (input) input.value = '';
    }
    return getCart()
      .then(function (cart) {
        var state = inspectBindings(cart);
        if (state.values.length === 0 && !state.malformed) return clearClaim();
        return null;
      })
      .catch(function () {
        /* best effort only */
      });
  }

  // ---------------------------------------------------------------------------
  // Resolver
  // ---------------------------------------------------------------------------

  /**
   * POST { type, credential } to the same-origin app proxy.
   * Resolves to { claim, expiresAt (ms), binding, initials, issuedBy,
   * confirmed: false } or rejects. The raw credential is never stored and
   * nothing from the response is logged. A resolution starts as a PENDING
   * confirmation; only confirmResolution() may mark it confirmed.
   */
  function resolveCredential(type, credential) {
    if (type !== 'pin' && type !== 'qr') return Promise.reject(new Error('persona_bad_type'));
    if (typeof credential !== 'string' || credential === '') {
      return Promise.reject(new Error('persona_bad_credential'));
    }
    return fetch(PROXY_PATH, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ type: type, credential: credential })
    })
      .then(function (response) {
        if (!response.ok) throw new Error('persona_resolver_unavailable');
        return response.json();
      })
      .then(function (data) {
        // Success requires exactly usable values for all four fields.
        if (!data || data.ok !== true) throw new Error('persona_resolver_rejected');
        var claim = data.claim;
        var binding = data.record_binding;
        var expiresAt = typeof data.expiresAt === 'string' ? Date.parse(data.expiresAt) : NaN;
        if (typeof claim !== 'string' || claim === '') throw new Error('persona_resolver_rejected');
        if (typeof binding !== 'string' || !BINDING_PATTERN.test(binding)) {
          throw new Error('persona_resolver_rejected');
        }
        if (!isFinite(expiresAt) || expiresAt <= Date.now()) {
          throw new Error('persona_resolver_rejected');
        }
        // The confirmation object is mandatory: without displayable record
        // details the customer cannot confirm, so the resolution fails closed.
        var confirmation = data.confirmation;
        var initials =
          confirmation && typeof confirmation.record_initials === 'string'
            ? confirmation.record_initials.trim()
            : '';
        var issuedBy =
          confirmation && typeof confirmation.issued_by === 'string'
            ? confirmation.issued_by.trim()
            : '';
        if (initials === '' || issuedBy === '') throw new Error('persona_resolver_rejected');
        return {
          claim: claim,
          expiresAt: expiresAt,
          binding: binding,
          initials: initials,
          issuedBy: issuedBy,
          confirmed: false
        };
      });
  }

  // ---------------------------------------------------------------------------
  // Explicit record confirmation (the only path that writes the claim for a
  // PIN/QR resolution and the only path that may mark it confirmed)
  // ---------------------------------------------------------------------------
  var confirmLocks = new WeakSet();

  function confirmResolution(container) {
    var resolution = getResolution(container);
    if (
      !resolution ||
      !resolution.claim ||
      !resolution.binding ||
      !resolution.initials ||
      !resolution.issuedBy
    ) {
      return Promise.reject(new Error('persona_resolution_expired'));
    }
    if (resolution.confirmed) return Promise.resolve(resolution);
    if (confirmLocks.has(container)) {
      return Promise.reject(new Error('persona_confirmation_in_progress'));
    }
    confirmLocks.add(container);
    return getCart()
      .then(function (cart) {
        if (getResolution(container) !== resolution) {
          throw new Error('persona_resolution_expired');
        }
        var bindings = inspectBindings(cart);
        if (bindings.malformed) throw new Error('persona_cart_binding_unusable');
        if (bindings.values.length === 1 && bindings.values[0] !== resolution.binding) {
          throw new Error('persona_binding_mismatch');
        }
        return writeClaim(resolution.claim);
      })
      .then(function () {
        // Expired between the claim write and here: fail closed. The orphan
        // claim is harmless without bound lines and the existing guards clear it.
        if (getResolution(container) !== resolution) {
          throw new Error('persona_resolution_expired');
        }
        resolution.confirmed = true;
        clearStaleFormMessage(container);
        return resolution;
      })
      .finally(function () {
        confirmLocks.delete(container);
      });
  }

  // ---------------------------------------------------------------------------
  // Cart helpers
  // ---------------------------------------------------------------------------
  /**
   * Locale-aware Ajax cart URL. Shopify exposes the localised root as
   * window.Shopify.routes.root and always ends it with '/' (e.g. '/en-ca/'),
   * so a plain join is correct; the trailing-slash check only guards against a
   * malformed override. Resolved per call because routes may be defined after
   * this asset loads. Falls back to '/' on the primary locale.
   */
  function cartUrl(path) {
    var routes = window.Shopify && window.Shopify.routes;
    var root = (routes && routes.root) || '/';
    return (root.charAt(root.length - 1) === '/' ? root : root + '/') + path;
  }

  function getCart() {
    return fetch(cartUrl('cart.js'), {
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    }).then(function (response) {
      if (!response.ok) throw new Error('persona_cart_unavailable');
      return response.json();
    });
  }

  /**
   * Distinct Persona bindings currently in the cart. `malformed` covers a
   * present-but-unusable binding value; both it and more than one distinct
   * value are fail-closed states.
   */
  function inspectBindings(cart) {
    var items = (cart && cart.items) || [];
    var values = [];
    var malformed = false;
    items.forEach(function (item) {
      var properties = (item && item.properties) || {};
      if (!Object.prototype.hasOwnProperty.call(properties, BINDING_PROPERTY)) return;
      var value = properties[BINDING_PROPERTY];
      // Only an exact empty string counts as absent; every other bad value fails closed.
      if (value === '') return;
      if (typeof value !== 'string' || !BINDING_PATTERN.test(value)) {
        malformed = true;
        return;
      }
      if (values.indexOf(value) === -1) values.push(value);
    });
    return { values: values, malformed: malformed || values.length > 1 };
  }

  function updateCartAttribute(value) {
    var attributes = {};
    attributes[CLAIM_ATTRIBUTE] = value;
    return fetch(cartUrl('cart/update.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: attributes })
    }).then(function (response) {
      if (!response.ok) throw new Error('persona_claim_write_failed');
      return true;
    });
  }

  function writeClaim(claim) {
    return updateCartAttribute(claim);
  }

  function clearClaim() {
    // An empty value removes a cart attribute.
    return updateCartAttribute('');
  }

  // ---------------------------------------------------------------------------
  // Messaging (textContent only — never innerHTML)
  // ---------------------------------------------------------------------------
  function showMessage(anchor, text) {
    if (!anchor) return;
    var existing = anchor.parentNode.querySelector(':scope > .persona-attribution-message');
    var element = existing;
    if (!element) {
      element = document.createElement('div');
      element.className = 'persona-pin-validation-message persona-attribution-message';
      element.setAttribute('role', 'alert');
      anchor.insertAdjacentElement('afterend', element);
    }
    element.textContent = text;
  }

  function clearMessage(anchor) {
    if (!anchor || !anchor.parentNode) return;
    var element = anchor.parentNode.querySelector(':scope > .persona-attribution-message');
    if (element) element.remove();
  }

  /**
   * The product-form guard message describes the state at the moment a submit
   * was blocked; any resolution-state transition (new resolution, confirm,
   * reject/clear, expiry) makes it stale, so those paths clear it here.
   */
  function clearStaleFormMessage(container) {
    var form = container && container.closest('form');
    if (!form) return;
    clearMessage(
      form.querySelector('.product-form__buttons') || form.querySelector('[type="submit"][name="add"]')
    );
  }

  // ---------------------------------------------------------------------------
  // Guarded resubmission
  // ---------------------------------------------------------------------------
  /**
   * Let one submit through the guard, exactly once.
   *
   * requestSubmit() dispatches the submit event synchronously, so the capture
   * listener normally consumes the flag inside this call. When the native
   * resubmit is blocked instead — constraint validation failing, an unsupported
   * or unassociated submitter, a listener cancelling it — no second event fires
   * and the flag would otherwise survive and let the NEXT user attempt bypass
   * the guard. The scheduled cleanup closes that hole; it only deletes a flag
   * that is still set, so it can never clobber a later legitimate bypass.
   *
   * Never uses form.submit(): that skips submit handlers and validation
   * entirely, which would defeat both the theme's Ajax add and this guard.
   */
  function resubmitOnce(form, submitter) {
    form.dataset.personaGuardCleared = 'true';

    var clearFlag = function () {
      if (form.dataset.personaGuardCleared === 'true') {
        delete form.dataset.personaGuardCleared;
      }
    };

    try {
      if (typeof form.requestSubmit === 'function') {
        // requestSubmit throws if the submitter is not associated with the form.
        if (submitter && submitter.form === form) {
          form.requestSubmit(submitter);
        } else {
          form.requestSubmit();
        }
      } else if (submitter) {
        submitter.click();
      }
    } finally {
      // Microtask runs first; the timeout covers environments without it.
      if (typeof queueMicrotask === 'function') queueMicrotask(clearFlag);
      setTimeout(clearFlag, 0);
    }
  }

  // ---------------------------------------------------------------------------
  // Product form guard (delegated, so replaced markup stays covered)
  // ---------------------------------------------------------------------------
  function personaFormState(form, container) {
    var resolution = resolutions.get(container);
    if (resolution) {
      if (!isLive(resolution)) return 'expired';
      // A pending (unconfirmed) resolution can never pass the add guard; the
      // customer must explicitly confirm the record first.
      return resolution.confirmed === true ? 'pin' : 'pending';
    }
    var skip = container.querySelector('.skip-engraving-checkbox');
    if (skip && skip.checked) return 'skip';
    var fingerprint = form.querySelector('input[name="properties[Fingerprint URL]"]');
    if (fingerprint && fingerprint.value.trim() !== '') return 'upload';
    return 'incomplete';
  }

  function guardProductSubmit(form, container) {
    var submitButton = form.querySelector('[type="submit"][name="add"]');
    var anchor = form.querySelector('.product-form__buttons') || submitButton;
    var state = personaFormState(form, container);
    var resolution = state === 'pin' ? getResolution(container) : null;

    clearMessage(anchor);

    if (state === 'expired') {
      showMessage(anchor, MESSAGES.expired);
      return;
    }
    if (state === 'pending') {
      showMessage(anchor, MESSAGES.confirmRequired);
      return;
    }
    if (state === 'incomplete') {
      showMessage(anchor, MESSAGES.incomplete);
      return;
    }

    if (submitButton) submitButton.setAttribute('aria-busy', 'true');

    getCart()
      .then(function (cart) {
        var bindings = inspectBindings(cart);
        if (bindings.malformed) throw new Error('persona_cart_binding_unusable');

        if (resolution) {
          if (bindings.values.length === 1 && bindings.values[0] !== resolution.binding) {
            throw new Error('persona_binding_mismatch');
          }
          return writeClaim(resolution.claim).then(function () {
            var input = form.querySelector(BINDING_INPUT_SELECTOR);
            if (!input) throw new Error('persona_binding_input_missing');
            input.value = resolution.binding;
          });
        }

        // Upload / skip paths carry no binding and must not leave a stale claim.
        var input = form.querySelector(BINDING_INPUT_SELECTOR);
        if (input) input.value = '';
        if (bindings.values.length === 0) return clearClaim();
        return null;
      })
      .then(function () {
        resubmitOnce(form, submitButton);
      })
      .catch(function (error) {
        showMessage(
          anchor,
          error && error.message === 'persona_binding_mismatch'
            ? MESSAGES.mismatch
            : error && error.message === 'persona_cart_binding_unusable'
              ? MESSAGES.unusable
              : MESSAGES.failed
        );
      })
      .finally(function () {
        if (submitButton) submitButton.removeAttribute('aria-busy');
      });
  }

  // ---------------------------------------------------------------------------
  // Cart / minicart checkout guard
  // ---------------------------------------------------------------------------
  function guardCheckoutSubmit(form, submitter) {
    var anchor =
      form.id === 'cart'
        ? document.getElementById('cart-errors')
        : document.querySelector('.minicart .drawer-bottom .subtotal');
    var fallbackAnchor = submitter && submitter.parentNode ? submitter : null;
    var target = anchor || fallbackAnchor;

    clearMessage(target);
    if (submitter) submitter.setAttribute('aria-busy', 'true');

    getCart()
      .then(function (cart) {
        var bindings = inspectBindings(cart);
        // Business-rule block, not a security control: a mismatched or
        // unreadable binding state must not reach checkout.
        if (bindings.malformed) throw new Error('persona_cart_binding_unusable');
        if (bindings.values.length === 0) {
          return clearClaim().catch(function () {
            /* best effort: an orphan claim is harmless without bound lines */
          });
        }
        return null;
      })
      .then(function () {
        resubmitOnce(form, submitter);
      })
      .catch(function (error) {
        showMessage(
          target,
          error && error.message === 'persona_cart_binding_unusable'
            ? MESSAGES.unusable
            : MESSAGES.failed
        );
      })
      .finally(function () {
        if (submitter) submitter.removeAttribute('aria-busy');
      });
  }

  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!form || form.tagName !== 'FORM') return;

      if (form.dataset.personaGuardCleared === 'true') {
        delete form.dataset.personaGuardCleared;
        return;
      }

      var container = form.querySelector('.persona-pin-container');
      if (container) {
        event.preventDefault();
        event.stopImmediatePropagation();
        guardProductSubmit(form, container);
        return;
      }

      if (form.id === 'cart' || form.id === 'minicart') {
        // Both checkout buttons are associated through form="…", so fall back to
        // that button when the browser reports no submitter: the re-submit must
        // carry the same checkout intent as the original.
        var submitter =
          event.submitter || document.querySelector('[name="checkout"][form="' + form.id + '"]');
        if (submitter && submitter.name !== 'checkout') return;
        event.preventDefault();
        event.stopImmediatePropagation();
        guardCheckoutSubmit(form, submitter);
      }
    },
    true
  );

  // ---------------------------------------------------------------------------
  // QR fragment handling
  // ---------------------------------------------------------------------------
  function takeQrTokenFromFragment() {
    // The early capture could not sanitize the URL, so the token may still be
    // sitting in the visible fragment. Refuse it outright rather than use a
    // credential that is already exposed.
    if ('__personaQrCaptureFailed' in window) {
      try {
        delete window.__personaQrCaptureFailed;
      } catch (e) {
        // Still fail closed below.
      }
      return null;
    }

    // The layout captures and strips the fragment before this file loads, so
    // prefer its single-use bridge. Direct fragment parsing stays below as the
    // fallback for any page that did not run the early capture.
    var bridge = window.__personaQrBridge;
    if (bridge && typeof bridge.take === 'function') {
      var early = null;
      try {
        early = bridge.take();
      } catch (e) {
        early = null;
      }
      if (early) return early;
    }

    var hash = window.location.hash || '';
    if (hash.indexOf(QR_FRAGMENT_KEY + '=') === -1) return null;
    var params = new URLSearchParams(hash.replace(/^#/, ''));
    var token = params.get(QR_FRAGMENT_KEY);
    // Strip the fragment before any network call so the token cannot leak
    // through history, referrers or a shared URL.
    params.delete(QR_FRAGMENT_KEY);
    var remainder = params.toString();
    window.history.replaceState(
      null,
      '',
      window.location.pathname + window.location.search + (remainder ? '#' + remainder : '')
    );
    return token || null;
  }

  function handleQrFragment() {
    var token = takeQrTokenFromFragment();
    if (!token) return;
    resolveCredential('qr', token)
      .then(function (resolution) {
        token = null;
        pendingQr = { resolution: resolution };
      })
      .catch(function () {
        token = null;
        pendingQr = { error: true };
      })
      .finally(function () {
        document.dispatchEvent(new CustomEvent('persona:qr-resolved'));
      });
  }

  function takePendingQr() {
    var value = pendingQr;
    pendingQr = null;
    return value;
  }

  /**
   * Guarantee the QR credential key is absent from the visible URL (hash and
   * query) without a reload. The fragment is already stripped before any
   * network call; this is the belt-and-braces cleanup used on rejection.
   */
  function removeQrFromUrl() {
    var hashParams = new URLSearchParams((window.location.hash || '').replace(/^#/, ''));
    var searchParams = new URLSearchParams((window.location.search || '').replace(/^\?/, ''));
    if (!hashParams.has(QR_FRAGMENT_KEY) && !searchParams.has(QR_FRAGMENT_KEY)) return;
    hashParams.delete(QR_FRAGMENT_KEY);
    searchParams.delete(QR_FRAGMENT_KEY);
    var search = searchParams.toString();
    var hash = hashParams.toString();
    window.history.replaceState(
      null,
      '',
      window.location.pathname + (search ? '?' + search : '') + (hash ? '#' + hash : '')
    );
  }

  window.PersonaAttributionV2 = {
    messages: MESSAGES,
    bindingProperty: BINDING_PROPERTY,
    resolveCredential: resolveCredential,
    confirmResolution: confirmResolution,
    setResolution: setResolution,
    getResolution: getResolution,
    clearResolution: clearResolution,
    removeQrFromUrl: removeQrFromUrl,
    getCart: getCart,
    inspectBindings: inspectBindings,
    takePendingQr: takePendingQr
  };

  handleQrFragment();
})();
